import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert,
  Table, Form, InputGroup
} from "react-bootstrap";
import {
  FaUserShield, FaFileDownload, FaSearch, FaFilter,
  FaFileAlt, FaCalendarAlt, FaTag, FaEye
} from "react-icons/fa";

const RegisteredAgentPage = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");

  useEffect(() => {
    fetchRegisteredAgentDocuments();
  }, []);

  const fetchRegisteredAgentDocuments = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch documents");

      const data = await response.json();
      setDocuments(data.user.registeredAgentDocuments || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (documentId) => {
    navigate(`/registered-agent/preview/${documentId}`);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredDocuments = documents
    .filter(doc => {
      const matchesSearch = doc.originalName.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.originalName.localeCompare(b.originalName);
        case 'size':
          return b.size - a.size;
        case 'date':
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">
          {error}
          <Button variant="link" onClick={fetchRegisteredAgentDocuments}>
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center mb-3">
            <FaUserShield className="me-3 text-primary" size={32} />
            <div>
              <h3 className="mb-1">Registered Agent Documents</h3>
              <p className="text-muted mb-0">
                Manage and view all your registered agent related documents
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      {/* <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FaFileAlt className="text-primary me-2" size={24} />
                <span className="h4 mb-0">{documents.length}</span>
              </div>
              <p className="text-muted mb-0">Total Documents</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FaTag className="text-success me-2" size={24} />
                <span className="h4 mb-0">
                  {documents.filter(doc => doc.documentType === 'annual-report').length}
                </span>
              </div>
              <p className="text-muted mb-0">Annual Reports</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FaFileDownload className="text-warning me-2" size={24} />
                <span className="h4 mb-0">
                  {documents.filter(doc => doc.documentType === 'compliance').length}
                </span>
              </div>
              <p className="text-muted mb-0">Compliance Docs</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FaCalendarAlt className="text-info me-2" size={24} />
                <span className="h4 mb-0">
                  {documents.length > 0 ? new Date(Math.max(...documents.map(doc => new Date(doc.date)))).getFullYear() : 'N/A'}
                </span>
              </div>
              <p className="text-muted mb-0">Latest Year</p>
            </Card.Body>
          </Card>
        </Col>
      </Row> */}

      {/* Filters and Search */}
      <Row className="mb-4">
        <Col md={4}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        {/* <Col md={3}>
          <Form.Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="annual-report">Annual Report</option>
            <option value="compliance">Compliance</option>
            <option value="legal">Legal</option>
            <option value="other">Other</option>
          </Form.Select>
        </Col> */}
        <Col md={3}>
          <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="size">Sort by Size</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button variant="outline-primary" onClick={fetchRegisteredAgentDocuments}>
            <FaFilter className="me-2" />
            Refresh
          </Button>
        </Col>
      </Row>

      {/* Documents Table */}
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">
            <FaUserShield className="me-2" />
            Your Registered Agent Documents
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {filteredDocuments.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Document Name</th>
                    <th>Size</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((document, index) => (
                    <tr key={index}>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaFileAlt className="me-2 text-muted" />
                          <span className="fw-medium">{document.originalName}</span>
                        </div>
                      </td>
                      <td>{formatFileSize(document.size || 0)}</td>
                      <td>{new Date(document.date).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handlePreview(document._id || document.id)}
                            title="Preview"
                          >
                            <FaEye />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5">
              <FaUserShield className="text-muted mb-3" style={{ fontSize: '4rem' }} />
              <h4 className="text-muted">No Documents Found</h4>
              <p className="text-muted">
                {searchTerm
                  ? 'Try adjusting your search criteria'
                  : 'No registered agent documents have been uploaded yet'}
              </p>
              {searchTerm && (
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    setSearchTerm('');
                  }}
                >
                  Clear Search
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

    </Container>
  );
};

export default RegisteredAgentPage;
